/**
 * Knowledge: Salesforge's Forge AI, with our offers and our brand file.
 *
 * The reference keeps its products and its knowledge base under one item in
 * the sidebar, as one page with two tabs and the tab's own primary button at
 * the right of the title row (`New product` on Products, `Add new file` on
 * Knowledge Base). So does this: `Page` titled Knowledge, tabs Offers and Brand
 * file, each a route, and each tab puts its button in the title row through
 * `KnowledgeActions`.
 *
 * Both screens existed already, one at `/settings/offers` and one as a section
 * of the brands screen, and both old paths redirect here. The brands screen
 * keeps its own copy of the brand file section, because that is where a
 * launch-blocked brand is spotted and fixed in one place.
 *
 * Each tab carries the `(?)` Salesforge puts after Products and Knowledge Base,
 * so the strip is the settings page's, which draws one. It lights the tab whose
 * path the address is under.
 */
import { createContext, useContext, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Outlet, useLocation } from 'react-router-dom'
import { IconFile, Page, SectionHeaders } from '../../components'
import { PageTabStrip } from '../settings/PageTabStrip'
import { pathIsUnder } from '../settings/settings-tabs-model'

export const KNOWLEDGE_TABS = [
  {
    to: '/knowledge/offers',
    label: 'Offers',
    help: 'What a brand can put in front of somebody, such as a case study or a report. The writer picks the one that fits each person.',
  },
  {
    to: '/knowledge/files',
    label: 'Brand file',
    help: 'One file per brand, kept as versions. A brand whose file does not validate cannot launch a sequence.',
  },
] as const

/** Where a tab's header buttons are drawn, once the title row has mounted. */
const ActionsSlot = createContext<HTMLElement | null>(null)
/** True from the first render inside the page, before the slot exists. */
const InKnowledgePage = createContext(false)

export function KnowledgeLayout(): JSX.Element {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null)
  const { pathname } = useLocation()
  const active = KNOWLEDGE_TABS.find((tab) => pathIsUnder(pathname, tab.to))?.to ?? null
  return (
    <Page
      icon={<IconFile />}
      title="Knowledge"
      help="What a brand can put in front of somebody, and what the writer knows about it."
      actions={<div ref={setSlot} className="flex items-center gap-3 empty:hidden" />}
      tabStrip={
        <PageTabStrip
          label="Knowledge"
          active={active}
          tabs={KNOWLEDGE_TABS.map((tab) => ({ key: tab.to, to: tab.to, label: tab.label, help: tab.help }))}
        />
      }
    >
      {/* `min-w-0` so a wide table scrolls inside its card rather than widening the layout. */}
      <div className="min-w-0">
        <InKnowledgePage.Provider value={true}>
          <ActionsSlot.Provider value={slot}>
            <SectionHeaders>
              <Outlet />
            </SectionHeaders>
          </ActionsSlot.Provider>
        </InKnowledgePage.Provider>
      </div>
    </Page>
  )
}

/**
 * A tab's buttons, drawn at the right of the Knowledge title row.
 *
 * Outside the layout (a test, or the editor mounted inside the brands screen)
 * there is no title row to draw into, so it draws nothing rather than
 * inventing a place for the buttons.
 */
export function KnowledgeActions({ children }: { children: ReactNode }) {
  const slot = useContext(ActionsSlot)
  return slot === null ? null : createPortal(children, slot)
}

/** Whether the screen is drawn inside the Knowledge page, so it can leave its own header out. */
export function useInKnowledgePage(): boolean {
  return useContext(InKnowledgePage)
}
